// Reminders + To Do (legacy desktop Company ▸ Reminders / To Do List; online books' Tasks).
// Reminders come from the books on every load; to-dos are the Treasurer's own (title, due date, notes).
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { money, fmtDate } from '../money';
import { Bell, CheckCircle2, AlertTriangle, Trash2, Plus, ArrowRight } from 'lucide-react';

const CARD = 'bg-white text-slate-800 border-slate-200';
const field = 'h-9 rounded border border-slate-300 bg-white px-2 text-sm text-slate-800';
const errOf = (e) => e?.response?.data?.detail || 'Request failed';

export function ReminderList({ rows, go, compact = false }) {
  if (!rows?.length) return null;
  return (
    <ul className={compact ? 'space-y-1' : 'divide-y divide-slate-100'} data-testid="reminder-list">
      {rows.map((r) => (
        <li key={r.kind + r.title} className={`flex items-center gap-2 text-sm ${compact ? '' : 'py-2'}`} data-testid={`reminder-${r.kind}`}>
          {r.level === 'alert' ? <AlertTriangle className="h-4 w-4 shrink-0 text-rose-500" /> : <Bell className="h-4 w-4 shrink-0 text-emerald-500" />}
          <span className="flex-1">
            {r.title}{r.count ? <b className="ml-1">({r.count})</b> : null}
            {r.amount != null && <span className="ml-1 text-slate-500 tabular-nums">{money(r.amount)}</span>}
            {r.due && <span className="ml-1 text-slate-500">· due {fmtDate(r.due)}</span>}
          </span>
          {r.go && <button className="text-emerald-600 hover:underline inline-flex items-center gap-0.5" onClick={() => go(r.go.view, r.go.arg || null)}>Open<ArrowRight className="h-3 w-3" /></button>}
        </li>
      ))}
    </ul>
  );
}

export function TasksPage({ base, go }) {
  const [data, setData] = useState(null);
  const [showDone, setShowDone] = useState(false);
  const [title, setTitle] = useState(''); const [due, setDue] = useState(''); const [notes, setNotes] = useState('');
  const [err, setErr] = useState('');
  const load = useCallback(() => axios.get(`${base}/tasks`, { params: { include_done: showDone } })
    .then((r) => setData(r.data)).catch((e) => setErr(errOf(e))), [base, showDone]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    setErr('');
    try { await axios.post(`${base}/tasks`, { title, due_date: due || null, notes }); setTitle(''); setDue(''); setNotes(''); load(); }
    catch (e) { setErr(errOf(e)); }
  };
  const setStatus = async (t, status) => { try { await axios.patch(`${base}/tasks/${t.id}`, { status }); load(); } catch (e) { setErr(errOf(e)); } };
  const del = async (t) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${t.title}"?`)) return;
    try { await axios.delete(`${base}/tasks/${t.id}`); load(); } catch (e) { setErr(errOf(e)); }
  };
  if (!data) return <div className="text-slate-500">Loading…</div>;
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="space-y-4 text-slate-800 max-w-4xl" data-testid="tasks-page">
      <h2 className="text-xl font-semibold flex items-center gap-2"><Bell className="h-5 w-5 text-emerald-600" />Reminders &amp; to-dos</h2>
      {err && <div className="rounded border border-rose-300 bg-rose-50 p-2 text-sm text-rose-700">{err}</div>}
      <div className={`rounded-lg border p-3 ${CARD}`}>
        <h3 className="font-semibold mb-1">Reminders</h3>
        <p className="text-xs text-slate-500 mb-2">From your books — they clear themselves when the work is done.</p>
        {data.reminders.length ? <ReminderList rows={data.reminders} go={go} />
          : <p className="flex items-center gap-2 text-sm text-slate-500"><CheckCircle2 className="h-4 w-4 text-emerald-500" />Nothing due.</p>}
      </div>
      <div className={`rounded-lg border p-3 ${CARD}`}>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold">To do</h3>
          <label className="text-xs text-slate-500 flex items-center gap-1"><input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} data-testid="tasks-show-done" />Show done</label>
        </div>
        <div className="flex flex-wrap items-end gap-2 mb-3">
          <label className="text-xs text-slate-500 flex-1 min-w-[200px]">To-do<Input className={`${field} w-full`} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Send the board the Q1 budget report" data-testid="task-title" /></label>
          <label className="text-xs text-slate-500">Due<Input type="date" className={field} value={due} onChange={(e) => setDue(e.target.value)} data-testid="task-due" /></label>
          <label className="text-xs text-slate-500 flex-1 min-w-[160px]">Notes<Input className={`${field} w-full`} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="task-notes" /></label>
          <Button className="bg-emerald-600 text-white" disabled={!title.trim()} onClick={add} data-testid="task-add"><Plus className="h-4 w-4 mr-1" />Add</Button>
        </div>
        {data.tasks.length === 0 ? <p className="text-sm text-slate-500">No to-dos.</p> : (
          <div className="max-h-[50vh] overflow-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 border-b border-slate-200"><th className="p-1.5 w-8" /><th className="p-1.5">To-do</th><th className="p-1.5">Due</th><th className="p-1.5">Notes</th><th className="p-1.5 w-8" /></tr></thead>
              <tbody>
                {data.tasks.map((t) => (
                  <tr key={t.id} className="border-b border-slate-100" data-testid={`task-row-${t.id}`}>
                    <td className="p-1.5"><input type="checkbox" checked={t.status === 'done'} onChange={(e) => setStatus(t, e.target.checked ? 'done' : 'open')} data-testid={`task-done-${t.id}`} /></td>
                    <td className={`p-1.5 ${t.status === 'done' ? 'line-through text-slate-400' : ''}`}>{t.title}</td>
                    <td className={`p-1.5 whitespace-nowrap ${t.status !== 'done' && t.due_date && t.due_date < today ? 'text-rose-600 font-medium' : ''}`}>{t.due_date ? fmtDate(t.due_date) : ''}</td>
                    <td className="p-1.5 text-slate-500">{t.notes}</td>
                    <td className="p-1.5"><button title="Delete" onClick={() => del(t)} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
